import { PlusCircleIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { EmptyPageCactusAnimation } from "@/components/animations/empty-cactus/index.react";
import { LegalEntityFrontDto } from "@/features/company/types";
import { useUser } from "@/features/user/hooks/use-user";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { Themes } from "@/types/theme";
import { AddCompanyModal } from "../AddCompanyModal";
import { CompanyCard } from "./CompanyCard";
import { usePrivy } from "@privy-io/react-auth";

export type Company = {
  title: string;
  address: {
    street: string;
    city: string;
    zipCode: string;
    country: string;
  };
  inscriptionNumber: string;
  olkypassLevel: number;
};

type CompanyListPropsTypes = {
  companies: LegalEntityFrontDto[] | undefined;
  addCompanyModal: () => void;
  closeCompanyModal: () => void;
  openModal: boolean;
  loading: boolean;
  allowedToCreateCompany: boolean;
};

export const CompanyList = ({
  companies,
  addCompanyModal,
  closeCompanyModal,
  openModal,
  loading,
  allowedToCreateCompany
}: CompanyListPropsTypes) => {
  const { t } = useTranslation(["pass", "atoms"]);
  const { getLanguage } = usei18n();
  const { useCountries } = useUser();
  const { data: countries } = useCountries(getLanguage());
  const { logout, authenticated } = usePrivy();
  const handleLogoutPriv = async () => {
    if (authenticated) await logout();
  };
  const getCountry = (c: string) =>
    countries?.countries.find((country) => {
      return country.id.toString() === c;
    });

  if (loading)
    return (
      <div className="flex h-[400px] justify-center">
        <div className="flex h-[400px] flex-col items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  if (!companies || companies?.length === 0)
    return (
      <div className="flex h-[400px] justify-center">
        <div className="flex h-[400px] flex-col items-center justify-center">
          <EmptyPageCactusAnimation />
          <Base className="mt-4 mb-2"> {t("business.label.empty")}</Base>
          <div
            data-tooltip={
              !allowedToCreateCompany ? t("atoms:molecules.kyc_alert.kyc_warning") : undefined
            }
          >
            <Button
              size="md"
              theme={Themes.primary}
              onClick={addCompanyModal}
              disabled={!allowedToCreateCompany}
              iconPosition="after"
              icon={<PlusCircleIcon />}
              dataCy="add-company-button"
            >
              {t("business.button.add_company")}
            </Button>
          </div>
        </div>
        <AddCompanyModal closeCompanyModal={closeCompanyModal} open={openModal} />
      </div>
    );

  return (
    <div className="flex flex-row flex-wrap gap-8">
      <AddCompanyModal closeCompanyModal={closeCompanyModal} open={openModal} />
      {companies &&
        companies?.length > 0 &&
        (companies ?? [])!.map((company, index) => (
          <div key={index}>
            <CompanyCard
              legal_entity_home_address={{
                ...company.legal_entity_home_address,
                country_id: getCountry(company?.legal_entity_home_address.country_id.toString())
                  ?.name as string
              }}
              legal_entity={company.legal_entity}
              legal_entity_olkypass={company.legal_entity_olkypass}
              key={company.legal_entity.RCSnumber}
              logoutPrivy={handleLogoutPriv}
            ></CompanyCard>
          </div>
        ))}
    </div>
  );
};
